import React, { useEffect } from "react";
import DetailHeaderCreateReport from "./DetailHeaderCreateReport";
import DetailHeaderLicensing from "./DetailHeaderLicensing";
import DetailHeaderDownload from "./DetailHeaderDownload";
import session from "models/sessionInstance";
import SearchableDropdown from "./SearchableDropdown";
import AddToReaderClient from "./addToReaderClient";
import I18n from "i18n";
import vent from "libraries/vent";
import DetailUpdateAvailable from "./DetailUpdateAvailable";
import Shortcutter from "libraries/shortcutter";
import appState from "models/appStateInstance";
import Tooltip from "./tooltip";

interface Props {
  agentResult: any; // Backbone.Model
  context?: string; // 'agents' or 'newsroom'
  readerResult?: any; // Backbone.Model - only for newsroom
}

export default function DatalakeHeaderActions({
  agentResult,
  context,
  readerResult,
}: Props) {
  const searchableDropdownProps = {
    onCategorySelect: (confId: number, catId: number) => {
      new AddToReaderClient(confId, catId).fromAgentResults([
        agentResult.get("id"),
      ]);
    },
  };

  const handleDeleteReaderResult = () => {
    if (!appState.isBusy()) {
      appState.requestStart();

      const onComplete = () => {
        appState.requestCompleted();
      };

      const opts = {
        success: onComplete,
        error: onComplete,
      };

      if (readerResult) {
        readerResult.destroy(opts);
      }
    } else {
      appState.displayWarning();
    }
  };

  const handleDeleteReaderResultAndTags = () => {
    if (!appState.isBusy()) {
      appState.requestStart();

      const onComplete = () => {
        appState.requestCompleted();
      };

      const opts = {
        success: onComplete,
        error: onComplete,
      };

      if (readerResult) {
        readerResult.destroyResultAndTags(opts);
      }
    } else {
      appState.displayWarning();
    }
  };
  useEffect(() => {
    // Shortcut: 'e' for edit
    const handleEdit = () => {
      if (
        session.hasFeature("agent_result_editing_possible") &&
        agentResult.isEditable()
      ) {
        vent.trigger("edit-form:start");
      }
    };
    Shortcutter.bindInModule(context, "e", handleEdit);

    // Delete shortcuts: 'del', '#', 'mod+backspace', 'x'
    const handleDelete = () => {
      if (agentResult.isOwnedByCurrentUser()) {
        agentResult.destroy();
      }
    };
    const deleteShortcuts = ["del", "#", "mod+backspace", "x"];
    Shortcutter.bindInModule(context, deleteShortcuts, handleDelete);

    // Cleanup shortcuts on unmount
    return () => {
      Shortcutter.unbindInModule(context, "e");
      Shortcutter.unbindInModule(context, deleteShortcuts);
    };
  }, [agentResult, context]);

  return (
    <>
      {agentResult.downloadable() && (
        <DetailHeaderDownload agentResult={agentResult} />
      )}
      {session.get("features").reports_select_clips_individually &&
        !!agentResult.get("clip_id") &&
        context === "agents" && (
          <DetailHeaderCreateReport agentResult={agentResult} />
        )}
      {agentResult.isOrderable() && (
        <DetailHeaderLicensing agentResult={agentResult} />
      )}
      {session.hasFeature("use_new_reader_management") &&
        context === "agents" && (
          <span className="link-bar__left-item">
            <SearchableDropdown {...searchableDropdownProps} />
          </span>
        )}
      {agentResult.isOwnedByCurrentUser() && context === "agents" && (
        <a
          className="link-bar__right-item"
          onClick={() => {
            agentResult.destroy();
          }}
        >
          <i className="icon-trash"></i>{" "}
          {I18n.t("webapp.agents.results.result_details.delete")}
        </a>
      )}
      {agentResult.isUpdatable() && agentResult.isOrdered() && (
        <DetailUpdateAvailable />
      )}
      {context === "newsroom" && (
        <>
          <span className="link-bar__right-item">
            <Tooltip
              toggle={<i className="icon-question-wrapped"></i>}
              tooltipBody={
                <>
                  <h1 className="tooltip__title">
                    {I18n.t("webapp.reader_admin.result.remove")}
                  </h1>
                  <div className="tooltip__text">
                    {I18n.t("webapp.reader_admin.result.remove_tooltip")}
                  </div>
                  <br />
                  <h1 className="tooltip__title">
                    {I18n.t(
                      "webapp.reader_admin.result.remove_and_delete_tags",
                    )}
                  </h1>
                  <div className="tooltip__text">
                    {I18n.t(
                      "webapp.reader_admin.result.remove_and_delete_tags_tooltip",
                    )}
                  </div>
                </>
              }
              positionMy="top right"
              positionAt="bottom center"
            />
          </span>
          <a
            className="link-bar__right-item"
            onClick={handleDeleteReaderResultAndTags}
            style={{ cursor: "pointer" }}
          >
            <i className="icon-trash"></i>{" "}
            {I18n.t("webapp.reader_admin.result.remove_and_delete_tags")}
          </a>
          <a
            className="link-bar__right-item"
            onClick={handleDeleteReaderResult}
            style={{ cursor: "pointer" }}
          >
            <i className="icon-eye"></i>{" "}
            {I18n.t("webapp.reader_admin.result.remove")}
          </a>
        </>
      )}
      {session.hasFeature("agent_result_editing_possible") &&
        agentResult.isEditable() && (
          <a
            className="link-bar__right-item"
            onClick={() => vent.trigger("edit-form:start")}
            style={{ cursor: "pointer" }}
          >
            <i className="icon-pencil"></i>{" "}
            {I18n.t("webapp.agents.results.result_details.edit_text")}
          </a>
        )}
    </>
  );
}
